import { syncAPI } from 'api/sync'
import * as posStore from 'store/posStore'
import { getOrCreatePosDeviceId, getPosDeviceName } from 'utils/posDeviceId'
import { checkConnectivity } from 'utils/connectivityCheck'
import { resolveOfflineScope } from 'store/posStore'

/**
 * POS device heartbeat publishing (Phase 9 multi-device close gate).
 */
const POS_HEARTBEAT_ENABLED = true
const HEARTBEAT_MIN_INTERVAL_MS = 30_000

let heartbeatInFlight: Promise<void> | null = null
let lastPublishedAt = 0

/** Publish local pending/failed POS order UUIDs so shift-close can block across terminals. */
export async function publishPosDeviceSyncHeartbeat(): Promise<void> {
  if (!POS_HEARTBEAT_ENABLED) return
  if (heartbeatInFlight) return heartbeatInFlight
  if (lastPublishedAt > 0 && Date.now() - lastPublishedAt < HEARTBEAT_MIN_INTERVAL_MS) return

  heartbeatInFlight = (async () => {
    if (!(await checkConnectivity())) return

    const scope = resolveOfflineScope()
    const branchId = scope.branchId
    if (!branchId || branchId === 'unknown') return

    const [pending, failed, activeShift] = await Promise.all([
      posStore.getPendingOrders(),
      posStore.getFailedOrders(),
      posStore.getCachedActiveShift<{ id?: string | number }>(branchId),
    ])

    const pendingUuids = pending
      .filter(
        (o) =>
          o.status === 'pending' || o.status === 'syncing' || o.status === 'conflict' || !o.status
      )
      .map((o) => o.client_uuid)
    const failedUuids = failed.map((f) => f.order.client_uuid)

    try {
      const { getAppVersionLabel } = await import('helpers/appVersion')
      await syncAPI.postDeviceHeartbeat({
        device_id: getOrCreatePosDeviceId(),
        device_name: getPosDeviceName(),
        branch_id: branchId,
        shift_id: activeShift?.id != null ? String(activeShift.id) : undefined,
        app_version: getAppVersionLabel().replace(/^v/, ''),
        pending_client_uuids: pendingUuids,
        failed_client_uuids: failedUuids
      })
      lastPublishedAt = Date.now()
    } catch {
      // Best-effort; do not break checkout/sync flows.
    }
  })()

  try {
    await heartbeatInFlight
  } finally {
    heartbeatInFlight = null
  }
}
